/**
 * Generate a lender-ready PDF packet (formal, scannable for underwriting).
 * Styling and branding aligned with LandlordOps brand guide and lender expectations.
 * Page 1: Confidential notice, borrower name, executive summary, key metrics, asset/address.
 * Page 2+: Trailing 12 Months (cash flow) and Portfolio Yield (rent roll).
 * Uses Asset and Portfolio Yield terminology; DSCR highlighted when ≥ 1.25.
 */

import jsPDF from "jspdf";

// Brand: Forest Green primary (#2C5F2D), dark slate text (#1E293B), gray secondary (#6B7280), success (#10B981)
const BRAND_PRIMARY_RGB = { r: 44, g: 95, b: 45 };
const TEXT_DARK_RGB = { r: 30, g: 41, b: 59 };
const TEXT_LIGHT_RGB = { r: 107, g: 114, b: 128 };
const SUCCESS_GREEN_RGB = { r: 16, g: 185, b: 129 };
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 18;
const FOOTER_H = 14;
const FONT = "helvetica";

/**
 * Draw document header on the current page. Brand bar: LandlordOps text mark left, title right, date under title.
 * Uses Helvetica Bold for the LandlordOps wordmark (jsPDF standard; Inter not embedded).
 */
function drawHeader(pdf, title = "LENDER PACKET") {
  pdf.setFillColor(
    BRAND_PRIMARY_RGB.r,
    BRAND_PRIMARY_RGB.g,
    BRAND_PRIMARY_RGB.b
  );
  pdf.rect(0, 0, PAGE_W, 24, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(14);
  pdf.text("LandlordOps", MARGIN, 12);
  pdf.setFontSize(13);
  pdf.text(title, PAGE_W - MARGIN - pdf.getTextWidth(title), 12);
  pdf.setFont(FONT, "normal");
  pdf.setFontSize(8);
  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  pdf.text(
    `Document date: ${dateStr}`,
    PAGE_W - MARGIN - pdf.getTextWidth(`Document date: ${dateStr}`),
    18
  );
  pdf.setDrawColor(226, 232, 240);
  pdf.line(0, 24, PAGE_W, 24);
}

/**
 * Draw footer on current page (call after all content; use setPage + drawFooter in a loop before save).
 * Formal lender wording: confidential notice, source, page numbers.
 */
function drawFooter(pdf, pageNum, totalPages) {
  pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
  pdf.setFontSize(8);
  pdf.setFont(FONT, "normal");
  const footerY = PAGE_H - 8;
  pdf.text("CONFIDENTIAL - FOR LENDER REVIEW", MARGIN, footerY);
  const center = "LandlordOps · Lender Packet";
  pdf.text(center, PAGE_W / 2 - pdf.getTextWidth(center) / 2, footerY);
  pdf.text(
    `Page ${pageNum} of ${totalPages}`,
    PAGE_W - MARGIN - pdf.getTextWidth(`Page ${pageNum} of ${totalPages}`),
    footerY
  );
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
}

/**
 * @param {number} n
 * @returns {string}
 */
function formatCurrency(n) {
  if (n == null || Number.isNaN(n)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * @param {Object} opts
 * @param {string} [opts.landlordName] - Borrower/landlord name for "Prepared for"
 * @param {string} [opts.address] - Property or portfolio address
 * @param {string} [opts.propertyPhotoUrl] - Optional image URL
 * @param {number} [opts.noi]
 * @param {number} [opts.dscr]
 * @param {number} [opts.occupancyRate]
 * @param {Array<{ month: string, grossRent: number, expenses: number, noi: number, cashFlow?: number }>} [opts.t12]
 * @param {Array<{ propertyName?: string, unitNumber: string, tenantName: string, monthlyRent?: number, rent?: number, status: string }>} [opts.rentRoll]
 * @param {string} [opts.filename] - Download filename
 */
export function generateLenderPacketPdf(opts = {}) {
  const {
    landlordName = "Borrower",
    address = "Portfolio Summary",
    propertyPhotoUrl,
    noi,
    dscr,
    occupancyRate,
    t12 = [],
    rentRoll = [],
    filename = `lender-packet-${new Date().toISOString().slice(0, 10)}.pdf`,
  } = opts;

  const pdf = new jsPDF("p", "mm", "a4");
  const asOfDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Page 1: Executive Summary (lender-focused)
  drawHeader(pdf, "LENDER PACKET");
  let y = 32;

  pdf.setFont(FONT, "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
  pdf.text(
    "CONFIDENTIAL - For lender underwriting review only. Do not distribute.",
    MARGIN,
    y
  );
  y += 6;

  pdf.setFont(FONT, "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
  pdf.text("Portfolio Summary for Lending Review", MARGIN, y);
  y += 8;

  pdf.setFont(FONT, "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
  pdf.text(`Prepared for: ${landlordName}`, MARGIN, y);
  y += 6;
  pdf.setFont(FONT, "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
  pdf.text(`As of: ${asOfDate}`, MARGIN, y);
  y += 12;

  // Asset / Address
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
  pdf.text("Door / Address", MARGIN, y);
  y += 5;
  pdf.setFont(FONT, "normal");
  pdf.text(address || "-", MARGIN, y);
  y += 10;

  // Optional property photo (compact)
  const imgW = 45;
  const imgH = 32;
  if (
    propertyPhotoUrl &&
    (propertyPhotoUrl.startsWith("data:") ||
      propertyPhotoUrl.startsWith("http"))
  ) {
    try {
      pdf.addImage(
        propertyPhotoUrl,
        "JPEG",
        PAGE_W - MARGIN - imgW,
        y - 8,
        imgW,
        imgH
      );
    } catch (e) {
      // skip if image fails
    }
  }

  // Key Metrics at a Glance (scannable box for lenders)
  y += 2;
  const boxY = y;
  const boxH = 28;
  pdf.setFillColor(248, 250, 252);
  pdf.rect(MARGIN, boxY, PAGE_W - 2 * MARGIN, boxH, "F");
  pdf.setDrawColor(226, 232, 240);
  pdf.rect(MARGIN, boxY, PAGE_W - 2 * MARGIN, boxH, "S");

  const col1 = MARGIN + 14;
  const col2 = PAGE_W / 2 - 10;
  const col3 = PAGE_W - MARGIN - 50;
  const row1 = boxY + 10;
  const row2 = boxY + 20;

  pdf.setFont(FONT, "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
  pdf.text("KEY METRICS AT A GLANCE", MARGIN + 8, boxY + 7);

  pdf.setFont(FONT, "normal");
  pdf.setFontSize(8);
  pdf.text("Net Operating Income (NOI)", col1 - 4, row1);
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
  pdf.text(formatCurrency(noi), col1 - 4, row2);

  pdf.setFont(FONT, "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
  pdf.text("DSCR", col2, row1);
  const dscrVal = dscr != null && !Number.isNaN(dscr) ? dscr.toFixed(2) : "-";
  const dscrMeet = typeof dscr === "number" && dscr >= 1.25;
  if (dscrMeet)
    pdf.setTextColor(
      SUCCESS_GREEN_RGB.r,
      SUCCESS_GREEN_RGB.g,
      SUCCESS_GREEN_RGB.b
    );
  else pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(11);
  pdf.text(dscrVal, col2, row2);
  if (dscrMeet) {
    pdf.setFont(FONT, "normal");
    pdf.setFontSize(7);
    pdf.text("✓ Meets 1.25+", col2, row2 + 5);
  }

  pdf.setFont(FONT, "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
  pdf.text("Occupancy", col3, row1);
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
  const occStr =
    occupancyRate != null && !Number.isNaN(occupancyRate)
      ? `${Number(occupancyRate)}%`
      : "-";
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(11);
  pdf.text(occStr, col3, row2);

  y = boxY + boxH + 12;
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
  pdf.setFont(FONT, "normal");
  pdf.setFontSize(9);

  // Portfolio summary from rent roll (units + monthly rent)
  const totalUnits = rentRoll.length;
  const totalMonthlyRent = rentRoll.reduce(
    (sum, r) => sum + (Number(r.monthlyRent ?? r.rent) || 0),
    0
  );
  if (totalUnits > 0) {
    pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
    pdf.setFontSize(9);
    const summaryLine = `Total assets: ${totalUnits} unit${
      totalUnits !== 1 ? "s" : ""
    } · Monthly rent (Portfolio Yield): ${formatCurrency(totalMonthlyRent)}`;
    pdf.text(summaryLine, MARGIN, y);
    pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
    y += 8;
  }

  pdf.setDrawColor(226, 232, 240);
  pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 10;

  pdf.setFontSize(8);
  pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
  pdf.text(
    "This document is confidential and prepared solely for lender underwriting. Data is derived from portfolio and rent roll",
    MARGIN,
    y
  );
  y += 4;
  pdf.text(
    "records in LandlordOps. Trailing 12 months (cash flow) and Portfolio Yield (current tenant list) follow on the next page(s).",
    MARGIN,
    y
  );

  // Page 2: T12 & Rent Roll
  pdf.addPage();
  y = 32;
  drawHeader(pdf, "LENDER PACKET");

  pdf.setFont(FONT, "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);
  pdf.text("1. Trailing 12 Months - Cash Flow", MARGIN, y);
  y += 10;

  const colW = [36, 30, 30, 30, 36];
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(255, 255, 255);
  pdf.setFillColor(
    BRAND_PRIMARY_RGB.r,
    BRAND_PRIMARY_RGB.g,
    BRAND_PRIMARY_RGB.b
  );
  pdf.rect(
    MARGIN,
    y - 5,
    colW[0] + colW[1] + colW[2] + colW[3] + colW[4],
    8,
    "F"
  );
  pdf.text("Month", MARGIN + 3, y + 1);
  pdf.text("Gross Rent", MARGIN + colW[0] + 3, y + 1);
  pdf.text("Expenses", MARGIN + colW[0] + colW[1] + 3, y + 1);
  pdf.text("NOI", MARGIN + colW[0] + colW[1] + colW[2] + 3, y + 1);
  pdf.text(
    "Cash Flow",
    MARGIN + colW[0] + colW[1] + colW[2] + colW[3] + 3,
    y + 1
  );
  y += 9;
  pdf.setFont(FONT, "normal");
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);

  if (t12.length === 0) {
    pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
    pdf.text("No trailing 12-month data available.", MARGIN + 3, y + 5);
    y += 14;
  } else {
    t12.forEach((row, i) => {
      if (y > PAGE_H - FOOTER_H - 20) {
        pdf.addPage();
        y = 32;
        drawHeader(pdf, "LENDER PACKET");
        y = 32;
      }
      pdf.setFillColor(i % 2 === 0 ? 248 : 252, 248, 252);
      pdf.rect(
        MARGIN,
        y - 4,
        colW[0] + colW[1] + colW[2] + colW[3] + colW[4],
        7,
        "F"
      );
      pdf.text(String(row.month || "-").slice(0, 11), MARGIN + 3, y + 2);
      pdf.text(
        formatCurrency(row.grossRent ?? row.income),
        MARGIN + colW[0] + 3,
        y + 2
      );
      pdf.text(
        formatCurrency(row.expenses),
        MARGIN + colW[0] + colW[1] + 3,
        y + 2
      );
      pdf.text(
        formatCurrency(row.noi),
        MARGIN + colW[0] + colW[1] + colW[2] + 3,
        y + 2
      );
      const cf =
        row.cashFlow ??
        (row.noi != null && row.debtService != null
          ? row.noi - row.debtService
          : row.noi ?? 0);
      pdf.text(
        formatCurrency(cf),
        MARGIN + colW[0] + colW[1] + colW[2] + colW[3] + 3,
        y + 2
      );
      y += 7;
    });
    y += 8;
  }

  // 2. Portfolio Yield (Rent Roll)
  if (y > PAGE_H - FOOTER_H - 70) {
    pdf.addPage();
    y = 32;
    drawHeader(pdf, "LENDER PACKET");
    y = 32;
  }
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(11);
  pdf.text("2. Portfolio Yield - Current Tenant List", MARGIN, y);
  y += 10;

  const rrColW = [38, 36, 48, 26, 30];
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(255, 255, 255);
  pdf.setFillColor(
    BRAND_PRIMARY_RGB.r,
    BRAND_PRIMARY_RGB.g,
    BRAND_PRIMARY_RGB.b
  );
  pdf.rect(
    MARGIN,
    y - 5,
    rrColW[0] + rrColW[1] + rrColW[2] + rrColW[3] + rrColW[4],
    8,
    "F"
  );
  pdf.text("Unit", MARGIN + 3, y + 1);
  pdf.text("Property", MARGIN + rrColW[0] + 3, y + 1);
  pdf.text("Tenant", MARGIN + rrColW[0] + rrColW[1] + 3, y + 1);
  pdf.text("Rent", MARGIN + rrColW[0] + rrColW[1] + rrColW[2] + 3, y + 1);
  pdf.text(
    "Status",
    MARGIN + rrColW[0] + rrColW[1] + rrColW[2] + rrColW[3] + 3,
    y + 1
  );
  y += 9;
  pdf.setFont(FONT, "normal");
  pdf.setTextColor(TEXT_DARK_RGB.r, TEXT_DARK_RGB.g, TEXT_DARK_RGB.b);

  if (rentRoll.length === 0) {
    pdf.setTextColor(TEXT_LIGHT_RGB.r, TEXT_LIGHT_RGB.g, TEXT_LIGHT_RGB.b);
    pdf.text("No rent roll data available.", MARGIN + 3, y + 5);
  } else {
    rentRoll.forEach((row, i) => {
      if (y > PAGE_H - FOOTER_H - 18) {
        pdf.addPage();
        y = 32;
        drawHeader(pdf, "LENDER PACKET");
        y = 32;
      }
      pdf.setFillColor(i % 2 === 0 ? 248 : 252, 248, 252);
      pdf.rect(
        MARGIN,
        y - 4,
        rrColW[0] + rrColW[1] + rrColW[2] + rrColW[3] + rrColW[4],
        7,
        "F"
      );
      pdf.text(String(row.unitNumber || "-").slice(0, 10), MARGIN + 3, y + 2);
      pdf.text(
        String(row.propertyName || "-").slice(0, 14),
        MARGIN + rrColW[0] + 3,
        y + 2
      );
      pdf.text(
        String(row.tenantName || "-").slice(0, 16),
        MARGIN + rrColW[0] + rrColW[1] + 3,
        y + 2
      );
      pdf.text(
        formatCurrency(row.monthlyRent ?? row.rent),
        MARGIN + rrColW[0] + rrColW[1] + rrColW[2] + 3,
        y + 2
      );
      pdf.text(
        String(row.status || "-").slice(0, 8),
        MARGIN + rrColW[0] + rrColW[1] + rrColW[2] + rrColW[3] + 3,
        y + 2
      );
      y += 7;
    });
  }

  // Add footers to all pages
  const totalPages = pdf.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    drawFooter(pdf, p, totalPages);
  }

  pdf.save(filename);
  return filename;
}
